"use client";

import { forwardRef, useEffect, useId, useImperativeHandle, useRef } from "react";

const SCRIPT_SRC = "https://o.alicdn.com/captcha-frontend/aliyunCaptcha/AliyunCaptcha.js";

declare global {
  interface Window {
    AliyunCaptchaConfig?: { region: string; prefix: string };
    initAliyunCaptcha?: (opts: Record<string, unknown>) => void;
    __aliyunCaptchaScriptLoaded?: boolean;
  }
}

/** onVerify 返回：captchaResult=阿里云验证是否通过，bizResult=业务是否通过 */
export interface CaptchaVerifyOutcome {
  captchaResult: boolean;
  bizResult: boolean;
  payload?: unknown;
}

/** 命令式句柄：业务按钮 onClick 调 trigger() 启动验证码流程 */
export interface AliyunCaptchaHandle {
  trigger: () => void;
}

interface AliyunCaptchaProps {
  /** 场景ID（控制台场景管理） */
  sceneId: string;
  /** 身份标识 prefix（控制台概览页） */
  prefix: string;
  /** 阿里云验证码参数 region */
  region?: "cn" | "sgp";
  mode?: "popup" | "embed";
  language?: "cn" | "tw" | "en";
  /** 业务回调：拿到 captchaVerifyParam 调后端，返回验证与业务结果 */
  onVerify: (captchaVerifyParam: string) => Promise<CaptchaVerifyOutcome>;
  /** 业务通过 */
  onBizSuccess?: (payload?: unknown) => void;
  /** 业务不通过（验证码已通过，业务侧失败，如邮箱已注册） */
  onBizError?: (payload?: unknown) => void;
  onReady?: () => void;
  onError?: (message: string) => void;
}

function loadScript(): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.__aliyunCaptchaScriptLoaded && window.initAliyunCaptcha) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
    if (existing) {
      if (window.initAliyunCaptcha) return resolve();
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("验证码脚本加载失败")));
      return;
    }
    const s = document.createElement("script");
    s.src = SCRIPT_SRC;
    s.async = true;
    const timeout = window.setTimeout(() => reject(new Error("验证码脚本加载超时")), 10000);
    s.onload = () => {
      window.clearTimeout(timeout);
      window.__aliyunCaptchaScriptLoaded = true;
      resolve();
    };
    s.onerror = () => {
      window.clearTimeout(timeout);
      reject(new Error("验证码脚本加载失败"));
    };
    document.head.appendChild(s);
  });
}

/**
 * 阿里云验证码 2.0 客户端组件。
 *
 * 触发机制：SDK 内部用 `button.onclick = verifyFn` 绑定触发器。若把 SDK 指向
 * React 渲染的 <Button>，受控输入引发的 re-render 会让 React/Base UI 把
 * button.onclick 重置为 noop，验证码回调永不被触发（表现为“点注册没反应”）。
 * 因此这里创建一个脱离 React 树的隐藏原生 <button> 作为 SDK 触发器，业务按钮
 * 通过 trigger() 调它的 .click() 启动验证，React 再怎么 re-render 都碰不到它。
 *
 * 无痕验证：通过则无感放行；存在风险则弹出滑块/拼图二次验证。
 */
export const AliyunCaptcha = forwardRef<AliyunCaptchaHandle, AliyunCaptchaProps>(
  function AliyunCaptcha(
    { sceneId, prefix, region = "cn", mode = "popup", language = "cn", onVerify, onBizSuccess, onBizError, onReady, onError },
    ref,
  ) {
    // useId 在 SSR/CSR 一致，去掉冒号以兼容 #id 选择器
    const elId = useId().replace(/:/g, "");
    const triggerId = `${elId}-trigger`;
    // 脱离 React 树的隐藏触发按钮（document.body 下，React 不接管其 onclick）
    const triggerBtnRef = useRef<HTMLButtonElement | null>(null);
    // 保存最后一次验证结果，供 onBizResultCallback 判断是否要展示业务错误
    const lastOutcomeRef = useRef<CaptchaVerifyOutcome | null>(null);
    // 用 ref 持有最新回调，避免 init 时闭包陈旧
    const onVerifyRef = useRef(onVerify);
    const onBizSuccessRef = useRef(onBizSuccess);
    const onBizErrorRef = useRef(onBizError);
    const onReadyRef = useRef(onReady);
    const onErrorRef = useRef(onError);
    onVerifyRef.current = onVerify;
    onBizSuccessRef.current = onBizSuccess;
    onBizErrorRef.current = onBizError;
    onReadyRef.current = onReady;
    onErrorRef.current = onError;

    // SDK 初始化完成标志；trigger() 在初始化前被调用时，初始化完成后自动补发
    const sdkReadyRef = useRef(false);
    const pendingTriggerRef = useRef(false);

    useEffect(() => {
      let cancelled = false;
      // 必须在脚本加载前设置全局身份标
      window.AliyunCaptchaConfig = { region, prefix };

      // 创建脱离 React 树的隐藏原生按钮，作为 SDK 的触发器。
      // display:none 不影响 .click() 派发事件；SDK 也只读 onclick，不读其几何位置。
      const btn = document.createElement("button");
      btn.type = "button";
      btn.id = triggerId;
      btn.style.display = "none";
      btn.setAttribute("aria-hidden", "true");
      document.body.appendChild(btn);
      triggerBtnRef.current = btn;

      loadScript().then(() => {
        if (cancelled || !window.initAliyunCaptcha) return;
        try {
          window.initAliyunCaptcha({
            SceneId: sceneId,
            mode,
            element: `#${elId}`,
            button: `#${triggerId}`,
            language,
            slideStyle: { width: 360, height: 40 },
            captchaVerifyCallback: async (captchaVerifyParam: string) => {
              const outcome = await onVerifyRef.current(captchaVerifyParam);
              lastOutcomeRef.current = outcome;
              return {
                captchaResult: outcome.captchaResult,
                bizResult: outcome.bizResult,
              };
            },
            onBizResultCallback: (bizResult: boolean) => {
              const last = lastOutcomeRef.current;
              if (bizResult) {
                onBizSuccessRef.current?.(last?.payload);
              } else if (last?.captchaResult) {
                // 验证码通过但业务失败 → 展示业务错误；验证码本身失败时由阿里云自动刷新重试
                onBizErrorRef.current?.(last?.payload);
              }
            },
            getInstance: () => {
              /* 实例由阿里云内部管理，无需持有 */
            },
          });
        } catch (error) {
          onErrorRef.current?.((error as Error).message || "验证码初始化失败");
          return;
        }

        // SDK 初始化完成，如果有 pending 的 trigger，立即补发
        sdkReadyRef.current = true;
        onReadyRef.current?.();
        if (pendingTriggerRef.current) {
          pendingTriggerRef.current = false;
          triggerBtnRef.current?.click();
        }
      }).catch((error) => {
        if (!cancelled) onErrorRef.current?.((error as Error).message || "验证码脚本加载失败");
      });

      return () => {
        cancelled = true;
        sdkReadyRef.current = false;
        pendingTriggerRef.current = false;
        btn.remove();
        triggerBtnRef.current = null;
      };
    }, [sceneId, prefix, elId, triggerId, region, mode, language]);

    useImperativeHandle(
      ref,
      () => ({
        trigger: () => {
          if (sdkReadyRef.current) {
            // SDK 已就绪，直接触发
            triggerBtnRef.current?.click();
          } else {
            // SDK 还在加载，标记 pending，加载完自动触发
            pendingTriggerRef.current = true;
          }
        },
      }),
      [],
    );

    // popup 模式下此容器作为弹窗锚点；保持 0 高度不占布局，但不可 display:none（会阻断弹窗渲染）
    return <div id={elId} aria-hidden="true" />;
  },
);
